import { useCurrentUserCommunityMembership } from 'features/community/hooks';
import { useCommunityContext } from 'providers/CommunityProvider';
import { useLegacyFeedPostCreateMutation, LegacyFeedPostCreateInput } from 'shared/graphql';
import { useCurrentUser, useCurrentUserPermissions, useToast } from 'shared/hooks';
import { recordError } from 'shared/utils/record';

/**
 * Create a new `LegacyFeedPost` instance.
 *
 * @returns The `create` function and the `loading` state.
 */
export function useLegacyFeedPostCreate() {
  const { userId } = useCurrentUser();
  const { selectedCommunity: community } = useCommunityContext();
  const { data: membership } = useCurrentUserCommunityMembership();
  const { isSchoolAdmin } = useCurrentUserPermissions();
  const { showError, showSuccess } = useToast();

  const [mutate, { loading }] = useLegacyFeedPostCreateMutation({
    refetchQueries: [
      'HomeFeedItems',
      'LegacyTimeStamps',
      'LegacyTimelineYears',
      'LegacyTimelineMonths',
      'LegacyTimelinePosts',
    ],
  });

  const create = async (data: Omit<LegacyFeedPostCreateInput, 'author' | 'school'>) => {
    if (!userId || !isSchoolAdmin) {
      return;
    }

    const payload: LegacyFeedPostCreateInput = {
      author: { connect: { id: userId } },
      school: { connect: { id: community?.id } },
      // https://github.com/jebelapp/jebel/issues/1842
      authorAffiliation: membership?.affiliation ?? data.authorAffiliation,

      ...data,
    };

    try {
      const response = await mutate({ variables: { data: payload } });

      showSuccess(
        community?.moderateLegacyPosts
          ? `Your post will be moderated, we'll let you know when it's available on the feed.`
          : `Your post has been created successfully.`,
      );

      return response;
    } catch (err) {
      recordError(err);
      showError('Something went wrong creating your post, please try again.');

      if (err instanceof Error) {
        showError(err.message);
      }
    }
  };

  return {
    create,
    loading,
  };
}
